import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import { assert, datetime } from '@codegrade/utils';
import * as models from '@/models';
import { CoursesStore } from '@/store';
import { isVariant } from '@/types';
import { emptyVNode, ifOrEmpty, maybeAttributeOrEllipsis } from '@/utils';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const maybeMakeStateBadge = (h, course, variant) => {
    const state = course.state;
    const cls = 'font-medium uppercase align-middle';
    switch (state) {
        case models.CourseState.visible:
            return Maybe.empty();
        case models.CourseState.archived:
            return Maybe.of(<b-badge class={cls} variant={variant}>
                    archived
                </b-badge>);
        case models.CourseState.deleted:
            return Maybe.of(<b-badge class={cls} variant={variant}>
                    deleted
                </b-badge>);
        default:
            if (!IS_PRODUCTION) {
                assert.AssertionError.assertNever(state);
            }
            return Maybe.empty();
    }
};
const CourseName = tsx.component({
    functional: true,
    props: {
        course: p(models.Course).required,
        bold: p(Boolean).default(false),
        hideTenant: p(Boolean).default(false),
        hideDate: p(Boolean).default(false),
        badgeVariant: p.ofType().validator(isVariant).default('primary'),
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render(h, ctx) {
        const { course, bold, badgeVariant, hideTenant, hideDate } = ctx.props;
        const counts = CoursesStore.getCourseCounts()(course);
        // XXX: This has a problem: we don't load all courses at once, so we
        // don't actually know if these counts are correct. However with
        // archiving it might be way more clear which course is which. We could
        // also simply add the year to the course unconditionally (maybe check
        // if it isn't already in the name?).
        let extra;
        if (!hideDate &&
            counts.total.length > 1 &&
            counts.byYear.get(course.createdAt.clone().local().year()).length > 1) {
            extra = `(${datetime.formatMoment(course.createdAt, 'YYYY-MM-DD')})`;
        }
        else if (!hideDate && counts.total.length > 1) {
            extra = `(${datetime.formatMoment(course.createdAt, 'YYYY')})`;
        }
        const stateBadge = maybeMakeStateBadge(h, course, badgeVariant);
        const showTenantBadge = !hideTenant && course.showTenant;
        const tenantAttr = maybeAttributeOrEllipsis(course.tenant, 'name');
        const tenantBadge = ifOrEmpty(showTenantBadge, () => (<b-badge class={['align-middle text-truncate max-w-1/1', { 'mr-2': stateBadge.isJust() }]}>
                {tenantAttr}
            </b-badge>));
        const titleParts = [course.name];
        if (extra != null) {
            titleParts.push(extra);
        }
        if (showTenantBadge) {
            titleParts.push(`[${tenantAttr}]`);
        }
        if (course.isArchived) {
            titleParts.push('[archived]');
        }
        const title = titleParts.join(' ');
        return (<span title={title} class={['course-name ', ctx.data.class, ctx.data.staticClass]}>
                <span>
                    <span class={{
                'font-weight-bold': bold,
                'align-middle': true,
                'mr-2': showTenantBadge || stateBadge.isJust(),
            }}>
                        {course.name}
                    </span>
                    {extra != null && <i class="align-middle inline-block"> {extra}</i>}
                </span>
                {tenantBadge}
                {stateBadge.orDefaultLazy(emptyVNode)}
            </span>);
    },
});
export default CourseName;
